import { VOInstrument, VOMelody, VOMelodyKind } from "../model/DataStructure.js";
import { AudioPlayer } from "./AudioPlayer.js";
import { GrainPlayer } from "./GrainPlayer.js";
import { MidiPlayer } from "./MidiPlayer.js";
import { Player, PlayerLoadOptions, PlayerState } from "./Player.js";

export interface MultiPlayerOptions {
    instruments: VOInstrument[];
    audioContext: AudioContext;
    stopInstruments: boolean;

    onComplete?: (m: Player) => void;
    onTime?: (m: Player) => void;
    onStateChange?: (m: Player) => void;
}

export class MultiPlayer implements Player {

    public onTime = e5.Signal.create<[Player]>();
    public onComplete = e5.Signal.create<[Player]>();
    public onStateChange = e5.Signal.create<[Player]>();

    private _current: Player = null;
    private _players: { [kind: string]: Player } = {};
    private _options: MultiPlayerOptions;

    constructor(options: MultiPlayerOptions) {
        options = e5.object.merge({
        }, options);
        this._options = options;

        // this.addPlayer("audio",
        //     new AudioPlayer({
        //         context: options.audioContext
        //     })
        // );

        this.addPlayer("audio",
            new GrainPlayer({
                audioContext: options.audioContext,
            })
        );

        this.addPlayer(["midi", "silence"],
            new MidiPlayer({
                audioContext: options.audioContext,
                instruments: options.instruments,
                stopInstruments: options.stopInstruments,
            })
        );

        if (typeof options.onComplete === "function") {
            this.onComplete.add(options.onComplete);
        }

        if (typeof options.onTime === "function") {
            this.onTime.add(options.onTime);
        }

        if (typeof options.onStateChange === "function") {
            this.onStateChange.add(options.onStateChange);
        }
    }

    private addPlayer(kind: VOMelodyKind | VOMelodyKind[], player: Player): void {
        if (!Array.isArray(kind)) {
            kind = [kind];
        }
        for (let k of kind) {
            this._players[k] = player;
        }
        player.onTime.add(() => {
            if (player !== this._current) {
                return;
            }
            this.onTime.dispatch(this);
        });

        player.onComplete.add(() => {
            if (player !== this._current) {
                return;
            }
            this.onComplete.dispatch(this);
        });

        player.onStateChange.add(() => {
            if (player !== this._current) {
                return;
            }
            this.onStateChange.dispatch(this);
        });
    }

    public get time(): number {
        if (!this._current) {
            return 0;
        }
        return this._current.time;
    }

    public set time(value: number) {
        if (!this._current) {
            return;
        }
        this._current.time = value;
    }

    public get duration(): number {
        if (!this._current) {
            return 0;
        }
        return this._current.duration;
    }

    public get state(): PlayerState {
        if (!this._current) {
            return "paused";
        }
        return this._current.state;
    }

    public get pitch(): number {
        if (!this._current) {
            return 0;
        }
        return this._current.pitch;
    }

    public set pitch(value: number) {
        if (!this._current) {
            return;
        }
        this._current.pitch = value;
    }

    public get speed(): number {
        if (!this._current) {
            return 0;
        }
        return this._current.speed;
    }

    public set speed(value: number) {
        if (!this._current) {
            return;
        }
        this._current.speed = value;
    }

    public play(): void {
        if (!this._current) {
            return;
        }
        this._current.play();
    }

    public pause(): void {
        if (!this._current) {
            return;
        }
        this._current.pause();
    }

    public stop(): void {
        if (!this._current) {
            return;
        }
        this._current.stop();
    }

    public load(melody: VOMelody, options?: PlayerLoadOptions): void {
        options = e5.object.merge<PlayerLoadOptions>(
            {
                pitch: 0,
                speed: 1
            }, options || ({} as any));

        if (!melody) {
            if (this._current) {
                this._current = null;
                this.onStateChange.dispatch(this);
            }
            return;
        }

        const isPlaying = this.state === "playing";

        let player = this._players[melody.kind];
        if (!player) {
            console.error("Could not find player for melody kind:", melody.kind);
            return;
        }

        if (this._current && this._current != player) {
            let c = this._current;
            this._current = null;
            c.load(null);
        }

        this._current = player;

        this.pitch = options.pitch;
        this.speed = options.speed;

        player.load(melody, options);

        if (isPlaying) {
            this.play();
        }
    }
}